/**
 * Reviews Anchor and Format Handler
 * - Formats review display from "(23 reviews)" to "4.9 (23)"
 * - Adds anchor functionality to scroll to reviews widget
 * - Waits for reviewsio widget to load before applying
 */

(function() {
  'use strict';

  /**
   * Extract rating and review count from title/aria-label
   * Format: "4.89 Stars - 36 Reviews"
   */
  function extractRatingAndReviews(element) {
    const title = element.getAttribute('title') || element.getAttribute('aria-label') || '';
    
    // Match pattern like "4.89 Stars - 36 Reviews"
    const match = title.match(/(\d+\.?\d*)\s+Stars?\s*-\s*(\d+)\s+Reviews?/i);
    
    if (match) {
      const rating = parseFloat(match[1]);
      const reviewCount = parseInt(match[2]);
      return { rating, reviewCount };
    }
    
    return null;
  }

  /**
   * Format the review text to "4.9 (23)"
   */
  function formatReviewText(rating, reviewCount) {
    // Round to 1 decimal place
    const roundedRating = Math.round(rating * 10) / 10;
    return `${roundedRating} (${reviewCount})`;
  }

  /**
   * Update review snippet format - only updates the span.ruk-rating-snippet-count text
   */
  function updateReviewFormat(parentElement) {
    // Try multiple possible selectors for the count span
    let countSpan = parentElement.querySelector('span.ruk-rating-snippet-count') ||
                    parentElement.querySelector('.ruk-rating-snippet-count') ||
                    parentElement.querySelector('span.ruk_rating_snippet_count') ||
                    parentElement.querySelector('.ruk_rating_snippet_count') ||
                    parentElement.querySelector('span[class*="rating-snippet-count"]');
    
    // If still not found, try to find any span that contains review count text
    if (!countSpan) {
      const allSpans = parentElement.querySelectorAll('span');
      for (let span of allSpans) {
        const text = span.textContent || '';
        // Look for patterns like "(23 reviews)" or "(23)" or "23 reviews"
        if (text.match(/\(?\d+\s*reviews?\)?/i)) {
          countSpan = span;
          break;
        }
      }
    }
    
    if (!countSpan) {
      return false;
    }
    
    // Get rating and review count from parent element's attributes
    const data = extractRatingAndReviews(parentElement);
    
    if (data) {
      const formattedText = formatReviewText(data.rating, data.reviewCount);
      
      // Only update the text content of the span
      countSpan.textContent = formattedText;
      
      return true;
    }
    
    return false;
  }

  /**
   * Add anchor functionality to scroll to reviews widget
   * Removes existing handlers and prevents default behavior
   */
  function addAnchorFunctionality(element) {
    // Remove any existing onclick handlers
    element.onclick = null;
    
    // Store reference to handler functions
    const clickHandler = function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      scrollToReviewsWidget();
      return false;
    };
    
    const keydownHandler = function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        scrollToReviewsWidget();
        return false;
      }
    };
    
    // Make it clickable
    element.setAttribute('role', 'button');
    element.setAttribute('tabindex', '0');
    
    // Add click handler with capture phase (true) to intercept early, before other handlers
    // This ensures our handler runs first and can prevent others
    element.addEventListener('click', clickHandler, true);
    
    // Add keyboard support with capture phase
    element.addEventListener('keydown', keydownHandler, true);
    
    // Add cursor pointer style
    element.style.cursor = 'pointer';
    
    // Also set onclick as backup to override any inline handlers
    element.onclick = clickHandler;
  }

  /**
   * Scroll to the reviews widget with header offset
   */
  function scrollToReviewsWidget() {
    const reviewsWidget = document.querySelector('reviewsio-product-reviews-widget');
    
    if (reviewsWidget) {
      // Get header height from CSS variable
      const headerHeight = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim() || 
        getComputedStyle(document.body)
        .getPropertyValue('--header-height')
        .trim() || 
        '0px';
      
      // Parse the header height value
      const headerHeightValue = parseFloat(headerHeight) || 0;
      
      // Calculate the target scroll position
      const widgetRect = reviewsWidget.getBoundingClientRect();
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      const targetScrollY = currentScrollY + widgetRect.top - headerHeightValue;
      
      // Scroll to position with smooth behavior
      window.scrollTo({
        top: targetScrollY,
        behavior: 'smooth'
      });
      
      // Add a slight highlight effect
      reviewsWidget.style.transition = 'box-shadow 0.3s ease';
      reviewsWidget.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.3)';
      
      setTimeout(() => {
        reviewsWidget.style.boxShadow = '';
      }, 2000);
    }
  }

  /**
   * Wait for reviewsio widget to load
   */
  function waitForReviewsWidget(callback, maxAttempts = 50, interval = 100) {
    let attempts = 0;
    
    const checkWidget = setInterval(() => {
      attempts++;
      
      const widget = document.querySelector('reviewsio-product-reviews-widget');
      
      if (widget || attempts >= maxAttempts) {
        clearInterval(checkWidget);
        callback(widget);
      }
    }, interval);
  }

  /**
   * Wait for review snippets to be fully loaded
   */
  function waitForReviewSnippets(callback, maxAttempts = 50, interval = 100) {
    let attempts = 0;
    
    const checkSnippets = setInterval(() => {
      attempts++;
      
      const snippets = document.querySelectorAll('.ruk_rating_snippet');
      const hasSnippets = snippets.length > 0;
      
      // Check if snippets have content (spans inside)
      let hasContent = false;
      if (hasSnippets) {
        snippets.forEach((snippet) => {
          if (snippet.querySelector('span')) {
            hasContent = true;
          }
        });
      }
      
      if ((hasSnippets && hasContent) || attempts >= maxAttempts) {
        clearInterval(checkSnippets);
        callback(snippets);
      }
    }, interval);
  }

  /**
   * Initialize review formatting and anchor functionality
   */
  function initReviewsAnchor() {
    // Wait for review snippets to load first, then wait for widget
    waitForReviewSnippets((reviewSnippets) => {
      if (reviewSnippets.length === 0) {
        return;
      }
      
      // Wait for reviews widget to load
      waitForReviewsWidget((widget) => {
        // Process each review snippet
        reviewSnippets.forEach((parentElement) => {
          // Update format of the span.ruk-rating-snippet-count
          if (updateReviewFormat(parentElement)) {
            // Add anchor functionality to parent element (returns new element)
            addAnchorFunctionality(parentElement);
          }
        });
      
        // Also watch for dynamically added review snippets
        if (typeof MutationObserver !== 'undefined') {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) { // Element node
                  // Check if the added node is a review snippet
                  if (node.classList && node.classList.contains('ruk_rating_snippet')) {
                    if (updateReviewFormat(node)) {
                      addAnchorFunctionality(node);
                    }
                  }
                  
                  // Check for review snippets within the added node
                  const snippets = node.querySelectorAll && node.querySelectorAll('.ruk_rating_snippet');
                  if (snippets && snippets.length > 0) {
                    snippets.forEach((parentElement) => {
                      if (updateReviewFormat(parentElement)) {
                        addAnchorFunctionality(parentElement);
                      }
                    });
                  }
                }
              });
            });
          });
          
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReviewsAnchor);
  } else {
    initReviewsAnchor();
  }
})();

